import { useMemo, type ReactNode } from 'react';

import {
  createTheme,
  CssBaseline,
  GlobalStyles,
  responsiveFontSizes,
  ThemeProvider,
  useMediaQuery,
} from '@mui/material';
import { darkThemeOptions, lightThemeOptions } from 'ml-react-component-library';
import { BrowserRouter } from 'react-router';

import { PermissionProvider } from '@/core/auth/permissions';
import { BreadcrumbProvider } from '@/shared/components/Breadcrumbs/BreadcrumbContext';

import { ApolloContextProvider } from '../ApolloContextProvider';
import { MsalContextProvider } from '../MsalContextProvider';

interface AppProviderProps {
  children: ReactNode;
}

/**
 * Typography scale bumped for Portal — base variants were 12 px (ML library
 * default), which read as too small at typical monitor distances.  We build
 * directly from the exported ThemeOptions so the AppBar colours and all other
 * component styles are preserved without reprocessing a compiled Theme object.
 */
const typographySizeUp = {
  body1: { fontSize: 14 },
  body2: { fontSize: 14 },
  button: { fontSize: 14 },
  caption: { fontSize: 13 },
  overline: { fontSize: 13 },
  subtitle2: { fontSize: 15 },
};

const baseLightTheme = createTheme(lightThemeOptions);
const lightTheme = responsiveFontSizes(
  createTheme(baseLightTheme, {
    typography: { ...baseLightTheme.typography, ...typographySizeUp },
    // components: {
    //   MuiCssBaseline: {
    //     styleOverrides: {
    //       html: { height: '100%' },
    //       body: { height: '100%' },
    //       '#root': { height: '100%' },
    //     },
    //   },
    // },
  }),
);

const baseDarkTheme = createTheme(darkThemeOptions);
const darkTheme = responsiveFontSizes(
  createTheme(baseDarkTheme, {
    typography: { ...baseDarkTheme.typography, ...typographySizeUp },
    // components: {
    //   MuiCssBaseline: {
    //     styleOverrides: {
    //       html: { height: '100%' },
    //       body: { height: '100%' },
    //       '#root': { height: '100%' },
    //     },
    //   },
    // },
  }),
);

export const AppProvider = ({ children }: AppProviderProps) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', {
    defaultMatches: false,
  });

  const theme = useMemo(() => (prefersDark ? darkTheme : lightTheme), [prefersDark]);

  return (
    <BrowserRouter>
      <MsalContextProvider>
        <PermissionProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles
              styles={{ html: { height: '100%' }, body: { height: '100%' }, '#root': { height: '100%' } }}
            />
            <ApolloContextProvider>
              <BreadcrumbProvider>{children}</BreadcrumbProvider>
            </ApolloContextProvider>
          </ThemeProvider>
        </PermissionProvider>
      </MsalContextProvider>
    </BrowserRouter>
  );
};
